import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react';
import { Box, Typography, Chip, Button, Tooltip, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import mermaid from 'mermaid';
import { useNotification } from '../contexts/NotificationContext';
import {
  canCopyImageToClipboard,
  copyPngBlobToClipboard,
  copyTextToClipboard,
  svgElementToPngBlob,
} from '../utils/clipboard';

let mermaidInitialized = false;
function ensureMermaidInitialized() {
  if (mermaidInitialized) return;
  mermaid.initialize({ startOnLoad: false, theme: 'default', securityLevel: 'strict' });
  mermaidInitialized = true;
}

const MAX_ATTRS_PER_ENTITY = 12;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

/** Nombres de columna/tabla en DB ya suelen ser identificadores validos; igual se sanitiza por si acaso. */
function sanitizeIdentifier(text, fallback) {
  const clean = String(text || '').replace(/[^a-zA-Z0-9_]/g, '_');
  return clean || fallback;
}

function escapeLabel(text) {
  return String(text).replace(/"/g, "'");
}

/** erDiagram muestra el id de la entidad como titulo de la caja, no admite un label aparte: se usa el nombre de tabla, deduplicando si dos tablas sanitizan igual (p. ej. mismo nombre en distinto schema). */
function buildEntityIds(tables) {
  const used = new Set();
  const byTableId = new Map();
  tables.forEach((t, i) => {
    let id = sanitizeIdentifier(t.name || t.label || t.id, `tabla_${i}`);
    while (used.has(id)) id = `${id}_${i}`;
    used.add(id);
    byTableId.set(t.id, id);
  });
  return byTableId;
}

/**
 * La columna identifica una sola fila de su tabla: PK marcada, "id" o "{tabla}_id"
 * (vsaas_assets.asset_id). Mismo criterio que resolveCatalogPrimaryKey del servidor;
 * las vistas de Timescale no traen PK declarada.
 */
function isKeyColumn(table, columnName) {
  const col = String(columnName || '').toLowerCase();
  if (!table || !col) return false;
  const cols = table.columns || [];
  if (cols.some((c) => c.isPrimaryKey && String(c.name).toLowerCase() === col)) return true;
  if (col === 'id') return true;
  const base = String(table.name || table.id || '')
    .split('.')
    .pop()
    .toLowerCase()
    .replace(/^vsaas_/, '');
  const singular = base.replace(/s$/, '');
  return col === `${singular}_id` || col === `${base}_id`;
}

/**
 * erDiagram: cada tabla lista (dentro de su caja) las columnas que participan en alguna
 * relacion, en vez de mostrar "columna origen -> columna destino" flotando sobre las lineas,
 * que queda ilegible con muchas relaciones cruzadas.
 */
function buildMermaidText(tables, relationships) {
  const nodeIdByTableId = buildEntityIds(tables);

  // Columnas relevantes por tabla: las que aparecen como origen o destino de alguna relacion.
  const attrsByTableId = new Map(tables.map((t) => [t.id, new Map()]));
  const addAttr = (tableId, columnName) => {
    const attrs = attrsByTableId.get(tableId);
    if (!attrs || attrs.has(columnName)) return;
    const table = tables.find((t) => t.id === tableId);
    const col = (table?.columns || []).find((c) => c.name === columnName);
    attrs.set(columnName, col || { name: columnName });
  };
  const tableById = new Map(tables.map((t) => [t.id, t]));
  const fkColumns = new Map();
  relationships.forEach((r) => {
    addAttr(r.fromTableId, r.fromColumn);
    addAttr(r.toTableId, r.toColumn);
    if (isKeyColumn(tableById.get(r.toTableId), r.toColumn)) {
      if (!fkColumns.has(r.fromTableId)) fkColumns.set(r.fromTableId, new Set());
      fkColumns.get(r.fromTableId).add(r.fromColumn);
    }
  });

  const lines = ['erDiagram'];
  tables.forEach((t) => {
    const nodeId = nodeIdByTableId.get(t.id);
    const attrs = Array.from(attrsByTableId.get(t.id)?.values() || []).slice(0, MAX_ATTRS_PER_ENTITY);
    lines.push(`  ${nodeId} {`);
    if (attrs.length === 0) {
      // erDiagram no permite una entidad sin atributos declarados.
      lines.push('    string sin_columnas_en_relaciones');
    }
    attrs.forEach((col) => {
      const type = sanitizeIdentifier(col.dataType, 'string');
      const name = sanitizeIdentifier(col.name, 'col');
      const key = isKeyColumn(t, col.name) ? ' PK' : col.isForeignKey || fkColumns.get(t.id)?.has(col.name) ? ' FK' : '';
      lines.push(`    ${type} ${name}${key}`);
    });
    lines.push('  }');
  });
  relationships.forEach((r) => {
    const from = nodeIdByTableId.get(r.fromTableId);
    const to = nodeIdByTableId.get(r.toTableId);
    if (!from || !to) return;
    const line = r.confirmed ? '--' : '..';
    const label = escapeLabel(`${r.fromColumn}/${r.toColumn}`);
    const fromKey = isKeyColumn(tableById.get(r.fromTableId), r.fromColumn);
    const toKey = isKeyColumn(tableById.get(r.toTableId), r.toColumn);
    // Lado "uno" = la columna que es clave de su tabla; sin clave en ningún lado queda N:N.
    const left = fromKey && !toKey ? '||' : '}o';
    const right = toKey ? '||' : 'o{';
    lines.push(`  ${from} ${left}${line}${right} ${to} : "${label}"`);
  });
  return lines.join('\n');
}

function ZoomControls({ zoom, onZoomIn, onZoomOut, onZoomReset }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Alejar">
        <span>
          <IconButton size="small" onClick={onZoomOut} disabled={zoom <= ZOOM_MIN} aria-label="Alejar">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="caption" sx={{ minWidth: 38, textAlign: 'center' }}>
        {Math.round(zoom * 100)}%
      </Typography>
      <Tooltip title="Acercar">
        <span>
          <IconButton size="small" onClick={onZoomIn} disabled={zoom >= ZOOM_MAX} aria-label="Acercar">
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Restablecer zoom">
        <span>
          <IconButton size="small" onClick={onZoomReset} disabled={zoom === 1} aria-label="Restablecer zoom">
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function DiagramCopyButtons({ onCopyText, onCopyImage, copyingImage, imageDisabled }) {
  return (
    <>
      <Tooltip title="Copia el diagrama en formato mermaid para pegarlo en otro lado (docs, Notion, GitHub, etc.)">
        <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={onCopyText}>
          Copiar texto
        </Button>
      </Tooltip>
      <Tooltip title="Copia el diagrama como imagen PNG para pegarlo en un correo, un doc o un chat">
        <span>
          <Button
            size="small"
            startIcon={<ImageOutlinedIcon fontSize="small" />}
            onClick={onCopyImage}
            disabled={imageDisabled || copyingImage}
          >
            Copiar imagen
          </Button>
        </span>
      </Tooltip>
    </>
  );
}

const DataModelDiagram = ({ tables = [], relationships = [] }) => {
  const { showSuccess, showError } = useNotification();
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [renderError, setRenderError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [copyingImage, setCopyingImage] = useState(false);
  const applyZoomDelta = (delta) =>
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 100) / 100)));
  const zoomIn = () => applyZoomDelta(ZOOM_STEP);
  const zoomOut = () => applyZoomDelta(-ZOOM_STEP);
  const zoomReset = () => setZoom(1);
  const rawId = useId();
  const mermaidId = `mermaid-model-diagram-${rawId.replace(/[^a-zA-Z0-9]/g, '')}`;

  const mermaidText = useMemo(() => buildMermaidText(tables, relationships), [tables, relationships]);
  const confirmedCount = relationships.filter((r) => r.confirmed).length;
  const pendingCount = relationships.length - confirmedCount;

  // Ctrl+scroll para zoom dentro del modal ampliado. Se usa un listener nativo (no
  // onWheel de React) con { passive: false } porque solo asi preventDefault() logra
  // frenar el zoom/scroll nativo del navegador. Se engancha via callback ref (en vez de
  // useEffect + ref) porque el nodo de DialogContent solo existe mientras el modal esta
  // montado, y un callback ref se dispara justo cuando React lo agrega/saca del DOM,
  // sin depender de que el timing de un efecto coincida con el de la animacion del modal.
  const handleWheelZoom = useCallback((e) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP / 2 : -ZOOM_STEP / 2;
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 100) / 100)));
  }, []);
  const wheelNodeRef = useRef(null);
  const modalContentRef = useCallback(
    (node) => {
      if (wheelNodeRef.current) {
        wheelNodeRef.current.removeEventListener('wheel', handleWheelZoom);
      }
      wheelNodeRef.current = node;
      if (node) {
        node.addEventListener('wheel', handleWheelZoom, { passive: false });
      }
    },
    [handleWheelZoom]
  );

  useEffect(() => {
    if (!tables.length) {
      setSvg('');
      return;
    }
    let cancelled = false;
    ensureMermaidInitialized();
    mermaid
      .render(mermaidId, mermaidText)
      .then(({ svg: renderedSvg }) => {
        if (!cancelled) {
          setSvg(renderedSvg);
          setRenderError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setRenderError(err.message || 'Error al generar el diagrama');
      });
    return () => {
      cancelled = true;
    };
  }, [mermaidText, mermaidId, tables.length]);

  const handleCopy = async () => {
    if (await copyTextToClipboard(mermaidText)) {
      showSuccess('Diagrama copiado (formato mermaid)');
    } else {
      showError('No se pudo copiar el diagrama');
    }
  };

  const handleCopyImage = async () => {
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl || copyingImage) return;
    setCopyingImage(true);
    const blobPromise = svgElementToPngBlob(svgEl);
    try {
      if (await copyPngBlobToClipboard(blobPromise)) {
        showSuccess('Imagen del diagrama copiada');
        return;
      }
      const blob = await blobPromise;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'diagrama-modelo.png';
      a.click();
      URL.revokeObjectURL(url);
      showError(
        canCopyImageToClipboard()
          ? 'El navegador no permitió copiar la imagen. Se descargó diagrama-modelo.png'
          : 'Copiar imágenes requiere abrir la app por https o localhost. Se descargó diagrama-modelo.png'
      );
    } catch (err) {
      showError(err.message || 'No se pudo copiar la imagen del diagrama');
    } finally {
      setCopyingImage(false);
    }
  };

  if (!tables.length) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 4,
          textAlign: 'center',
          bgcolor: 'grey.50',
          height: '100%',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Selecciona tablas en el panel izquierdo o en el desplegable para construir el modelo.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: '#f8fafc',
        overflow: 'auto',
        height: '100%',
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <Chip size="small" label={`${tables.length} tablas`} />
        <Chip size="small" color="primary" label={`${confirmedCount} relaciones activas`} />
        {pendingCount > 0 && <Chip size="small" color="warning" label={`${pendingCount} pendientes`} variant="outlined" />}
        <Box sx={{ flex: 1 }} />
        <ZoomControls zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} onZoomReset={zoomReset} />
        <DiagramCopyButtons
          onCopyText={handleCopy}
          onCopyImage={handleCopyImage}
          copyingImage={copyingImage}
          imageDisabled={!svg}
        />
        <Tooltip title="Ampliar diagrama">
          <IconButton size="small" onClick={() => setExpanded(true)} aria-label="Ampliar diagrama">
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1 }}>
        {renderError ? (
          <Typography variant="body2" color="error">
            {renderError}
          </Typography>
        ) : (
          <div
            ref={containerRef}
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        )}
      </Box>

      <Dialog open={expanded} onClose={() => setExpanded(false)} maxWidth={false} fullWidth
        PaperProps={{ sx: { width: '95vw', height: '92vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span" sx={{ flex: 1 }}>Vista de diagrama de relaciones</Typography>
          <ZoomControls zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} onZoomReset={zoomReset} />
          <DiagramCopyButtons
            onCopyText={handleCopy}
            onCopyImage={handleCopyImage}
            copyingImage={copyingImage}
            imageDisabled={!svg}
          />
          <IconButton onClick={() => setExpanded(false)} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          ref={modalContentRef}
          dividers
          sx={{
            overflow: 'auto',
            bgcolor: '#f8fafc',
            p: 2,
            // El SVG que genera mermaid trae un max-width fijo en px (pensado para el
            // panel chico); en el modal grande lo sobreescribimos para que sí aproveche
            // el espacio extra en vez de quedar del mismo tamaño con mas scroll alrededor.
            '& svg': { maxWidth: 'none !important', width: '100%', height: 'auto' },
          }}
        >
          {renderError ? (
            <Typography variant="body2" color="error">
              {renderError}
            </Typography>
          ) : (
            <div
              style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DataModelDiagram;
